/**
 * Renderer for the setup-guide reference sections (device support, dual-SIM
 * behaviour, install dead-ends, aftercare).
 *
 * Lives in `shared` because BOTH sides render this page and they must not
 * drift: `backend/src/util/guides-body.ts` server-renders it for crawlers and
 * the pre-hydration paint, and `frontend/src/routes/guides.ts` re-renders the
 * whole route on hydration — which silently discards anything the server drew
 * that the client does not know how to redraw. Duplicating the markup in two
 * packages is how that divergence starts, so there is one function.
 *
 * Content source: the `guides.sections` subtree of the i18n bundles
 * (frontend/src/i18n/<locale>.json), mirrored into data/guides-content.json
 * for the backend by scripts/seo/build-guides-content.mjs.
 */
function escapeText(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/**
 * Escape everything, then re-admit the small inline set the guide copy uses.
 * Content is repo-authored, but it still goes through escaping so a stray
 * angle bracket in a menu path (`LPA:1$<server>$<code>`) renders as text
 * instead of eating the rest of the paragraph.
 */
function escapeRich(s) {
    return escapeText(s).replace(/&lt;(\/?)(strong|em|code)&gt;/g, '<$1$2>');
}
const slug = (s) => s.replace(/[^a-z0-9-]/gi, '').slice(0, 48);
/**
 * @param sections  Section list for one device, in reading order.
 * @param labelledBy Optional id of the heading that introduces the group.
 */
export function renderGuideSections(sections) {
    if (!sections?.length)
        return '';
    const blocks = sections
        .filter((s) => s.heading)
        .map((s) => {
        const id = slug(s.id ?? '');
        const paras = (s.paragraphs ?? [])
            .filter((p) => p && p.trim())
            .map((p) => `<p class="guide-note__para">${escapeRich(p)}</p>`)
            .join('');
        const bullets = (s.bullets ?? []).filter((b) => b.label && b.body);
        // A definition list, not a card grid: each row is a term and its
        // explanation, which is what this content actually is.
        const list = bullets.length
            ? `<dl class="guide-note__list">${bullets
                .map((b) => `<dt class="guide-note__term">${escapeRich(b.label ?? '')}</dt>`
                + `<dd class="guide-note__def">${escapeRich(b.body ?? '')}</dd>`)
                .join('')}</dl>`
            : '';
        const headingId = id ? ` id="${id}"` : '';
        return `<section class="guide-note"${headingId}>`
            + `<h2 class="guide-note__h">${escapeText(s.heading ?? '')}</h2>`
            + paras + list
            + '</section>';
    })
        .join('');
    return blocks ? `<div class="guide-notes">${blocks}</div>` : '';
}
